import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  User
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, googleProvider, db } from './firebase';

export interface AuthUserProfile {
  uid: string;
  name: string;
  email: string;
  photoURL: string | null;
  createdAt?: string;
  updatedAt?: string;
  lastLoginAt?: string;
}

// Ensure persistence is set to browser local storage
setPersistence(auth, browserLocalPersistence).catch((err) => {
  console.warn('Could not enable browserLocalPersistence for Firebase Auth:', err);
});

// Configure Google Auth Provider
googleProvider.setCustomParameters({
  prompt: 'select_account',
});

/**
 * Initiates Google Sign-In via popup
 */
export async function signInWithGoogle(): Promise<{ user: User | null; error: string | null }> {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    if (user) {
      await syncUserProfile(user);
    }

    return { user, error: null };
  } catch (error: any) {
    console.error('Error during Google Sign-In:', error);
    let errorMessage = 'Error al iniciar sesión con Google.';
    if (error.code === 'auth/popup-closed-by-user') {
      errorMessage = 'El diálogo de inicio de sesión fue cerrado antes de completar.';
    } else if (error.code === 'auth/cancelled-popup-request') {
      errorMessage = 'Se canceló la solicitud de inicio de sesión.';
    } else if (error.code === 'auth/network-request-failed') {
      errorMessage = 'Error de conexión de red al conectar con Google.';
    } else if (error.message) {
      errorMessage = error.message;
    }
    return { user: null, error: errorMessage };
  }
}

/**
 * Signs out current user from Firebase Auth
 */
export async function signOutUser(): Promise<{ success: boolean; error: string | null }> {
  try {
    await firebaseSignOut(auth);
    return { success: true, error: null };
  } catch (error: any) {
    console.error('Error during Sign-Out:', error);
    return { success: false, error: error?.message || 'Error al cerrar sesión' };
  }
}

/**
 * Returns the current Firebase ID Token for authenticating backend requests
 */
export async function getCurrentIdToken(forceRefresh: boolean = false): Promise<string | null> {
  const currentUser = auth.currentUser;
  if (!currentUser) return null;
  try {
    return await currentUser.getIdToken(forceRefresh);
  } catch (error) {
    console.error('Error getting ID token:', error);
    return null;
  }
}

/**
 * Subscribes to Firebase Auth state changes
 */
export function subscribeToAuth(callback: (user: User | null) => void) {
  return onAuthStateChanged(auth, (user) => {
    callback(user);
  });
}

/**
 * Synchronizes user profile basic metadata in `users/{uid}` in Firestore
 */
export async function syncUserProfile(user: User): Promise<AuthUserProfile> {
  const userRef = doc(db, 'users', user.uid);
  const now = new Date().toISOString();

  let existingData: any = null;
  try {
    const snap = await getDoc(userRef);
    if (snap.exists()) {
      existingData = snap.data();
    }
  } catch (e) {
    console.warn('Could not read existing user doc from Firestore:', e);
  }

  const profileData: AuthUserProfile = {
    uid: user.uid,
    name: user.displayName || user.email?.split('@')[0] || 'Usuario',
    email: user.email || '',
    photoURL: user.photoURL || null,
    createdAt: existingData?.createdAt || now,
    updatedAt: now,
    lastLoginAt: now,
  };

  try {
    await setDoc(userRef, {
      ...profileData,
      lastLoginTimestamp: serverTimestamp(),
    }, { merge: true });
  } catch (err) {
    console.warn('Could not write user profile to Firestore (may be offline or awaiting rules):', err);
  }

  return profileData;
}

/**
 * Log out helper alias
 */
export const logOutFromFirebase = signOutUser;
